import React, { useState, useEffect } from 'react';
import CameraScanner from './CameraScanner';
import { uploadTagImage } from '../lib/r2Storage';



const InstrumentTags = ({ user, instruments, setInstruments, supabase }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isDbError, setIsDbError] = useState(false);

  // Form states for Create/Edit Tag
  const [tagId, setTagId] = useState(null); // Null for creating, number/string for editing
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Barcode linking states
  const [selectedTagId, setSelectedTagId] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  // Search filter for linked list
  const [filterSearch, setFilterSearch] = useState('');

  // Fetch tags on mount
  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      if (supabase) {
        const { data, error } = await supabase.from('instrument_tags').select('*').order('id', { ascending: true });
        if (error) throw error;
        setTags(data || []);
        setIsDbError(false);
      } else {
        throw new Error('Supabase client not initialized');
      }
    } catch (err) {
      console.warn("Could not fetch from Supabase, falling back to localStorage:", err.message);
      setIsDbError(true);
      const localTags = localStorage.getItem('mock_instrument_tags');
      if (localTags) {
        setTags(JSON.parse(localTags));
      } else {
        // Initial mock tags
        const initialMock = [
          { id: 1, name: 'คีมถอนฟันหน้า (Anterior Forceps)', description: 'สำหรับถอนฟันตัดหน้าและฟันเขี้ยวบน คาดแถบสีแดงที่บาร์โค้ด', image_url: 'https://images.unsplash.com/photo-1579684389782-64d84b5e901a?auto=format&fit=crop&q=80&w=200', created_at: new Date().toISOString() },
          { id: 2, name: 'ชุดตรวจพื้นฐาน (Diagnostic Set)', description: 'ประกอบด้วยกระจกส่องปาก, explorer, และ forceps คาดแถบสีฟ้า', image_url: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&q=80&w=200', created_at: new Date().toISOString() }
        ];
        localStorage.setItem('mock_instrument_tags', JSON.stringify(initialMock));
        setTags(initialMock);
      }
    } finally {
      setLoading(false);
    }
  };

  // Cloudinary image upload logic (matches App.js)
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);

    try {
      const secureUrl = await uploadTagImage(file);
      setImageUrl(secureUrl);
      alert("📸 อัปโหลดรูป tag สำเร็จ!");
    } catch (err) {
      console.error("Storage Upload Error:", err);
      alert("❌ เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ: " + (err.message || ""));
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setImageUrl('');
    setTagId(null);
    setIsEditing(false);
  };

  const handleSaveTag = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert("กรุณาระบุชื่อ Tag");
    setSubmitting(true);

    try {
      if (isDbError || !supabase) {
        // LocalStorage logic
        let localTags = [...tags];
        if (isEditing) {
          localTags = localTags.map(t => t.id === tagId ? { ...t, name, description, image_url: imageUrl } : t);
        } else {
          const newId = localTags.length > 0 ? Math.max(...localTags.map(t => t.id)) + 1 : 1;
          localTags.push({ id: newId, name, description, image_url: imageUrl, created_at: new Date().toISOString() });
        }
        localStorage.setItem('mock_instrument_tags', JSON.stringify(localTags));
        setTags(localTags);
        alert(isEditing ? "แก้ไข Tag สำเร็จ" : "เพิ่ม Tag สำเร็จ");
      } else {
        // Supabase logic
        if (isEditing) {
          const { error } = await supabase.from('instrument_tags').update({ name, description, image_url: imageUrl }).eq('id', tagId);
          if (error) throw error;
          alert("แก้ไข Tag สำเร็จ");
        } else {
          const { error } = await supabase.from('instrument_tags').insert([{ name, description, image_url: imageUrl }]);
          if (error) throw error;
          alert("เพิ่ม Tag สำเร็จ");
        }
        await fetchTags();
      }
      resetForm();
    } catch (err) {
      console.error("Error saving tag:", err);
      alert("เกิดข้อผิดพลาด: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (tag) => {
    setTagId(tag.id);
    setName(tag.name);
    setDescription(tag.description || '');
    setImageUrl(tag.image_url || '');
    setIsEditing(true);
    // Scroll to form smoothly
    const formElement = document.getElementById('tag-editor-form');
    if (formElement) formElement.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteTag = async (id) => {
    const targetTag = tags.find(t => t.id === id);
    if (targetTag && targetTag.name.toUpperCase() === 'NON-STERILE') {
      alert("❌ ไม่สามารถลบ Tag ระบบ (NON-STERILE) ได้");
      return;
    }

    if (!window.confirm("คุณแน่ใจหรือไม่ที่จะลบ Tag นี้? ข้อมูลการผูกบาร์โค้ดที่อ้างอิง Tag นี้จะยังคงอยู่ แต่อาจไม่แสดงรูปภาพ")) return;

    try {
      if (isDbError || !supabase) {
        const localTags = tags.filter(t => t.id !== id);
        localStorage.setItem('mock_instrument_tags', JSON.stringify(localTags));
        setTags(localTags);
        alert("ลบ Tag สำเร็จ");
      } else {
        const { error } = await supabase.from('instrument_tags').delete().eq('id', id);
        if (error) throw error;
        alert("ลบ Tag สำเร็จ");
        await fetchTags();
      }
    } catch (err) {
      console.error("Error deleting tag:", err);
      alert("เกิดข้อผิดพลาด: " + err.message);
    }
  };

  // Link Tag to Barcode
  const handleLinkBarcode = async (e) => {
    e.preventDefault();
    if (!selectedTagId) return alert("กรุณาเลือก Tag");
    if (!barcodeInput.trim()) return alert("กรุณาระบุรหัสบาร์โค้ด");

    const barcode = barcodeInput.trim();
    // ค้นหาอุปกรณ์ใน state
    const instrument = instruments.find(i => i.unique_barcode === barcode);
    if (!instrument) {
      return alert(`❌ ไม่พบเครื่องมือรหัส "${barcode}" ในระบบ กรุณาตรวจสอบรหัสอีกครั้ง`);
    }

    try {
      const updatedDetails = {
        ...instrument.details,
        tag_id: Number(selectedTagId)
      };

      if (supabase) {
        const { error } = await supabase
          .from('instruments')
          .update({ details: updatedDetails, last_updated: new Date().toISOString() })
          .eq('id', instrument.id);

        if (error) throw error;
      }

      // Update parent component state
      setInstruments(prev => prev.map(i => i.id === instrument.id ? { ...i, details: updatedDetails } : i));
      alert(`🎉 ผูกบาร์โค้ด ${barcode} เข้ากับ Tag สำเร็จ!`);
      setBarcodeInput('');
    } catch (err) {
      console.error("Error linking barcode:", err);
      alert("เกิดข้อผิดพลาด: " + err.message);
    }
  };

  // Unlink Tag from Barcode
  const handleUnlinkBarcode = async (instrument) => {
    if (!window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการผูก Tag ของบาร์โค้ด ${instrument.unique_barcode}?`)) return;

    try {
      const updatedDetails = { ...instrument.details };
      delete updatedDetails.tag_id;

      if (supabase) {
        const { error } = await supabase
          .from('instruments')
          .update({ details: updatedDetails, last_updated: new Date().toISOString() })
          .eq('id', instrument.id);

        if (error) throw error;
      }

      setInstruments(prev => prev.map(i => i.id === instrument.id ? { ...i, details: updatedDetails } : i));
      alert("ยกเลิกการผูก Tag สำเร็จ");
    } catch (err) {
      console.error("Error unlinking barcode:", err);
      alert("เกิดข้อผิดพลาด: " + err.message);
    }
  };

  const handleScannerSuccess = (decodedText) => {
    setBarcodeInput(decodedText);
    setIsScannerOpen(false);
  };

  // Get associated instruments
  const getLinkedInstruments = () => {
    return instruments.filter(i => {
      const tagIdInDetails = i.details?.tag_id;
      if (!tagIdInDetails) return false;
      
      const matchSearch = filterSearch.trim().toLowerCase();
      if (!matchSearch) return true;

      const tag = tags.find(t => String(t.id) === String(tagIdInDetails));
      const tagName = tag ? tag.name : '';
      return (
        i.unique_barcode.toLowerCase().includes(matchSearch) ||
        i.item_name.toLowerCase().includes(matchSearch) ||
        tagName.toLowerCase().includes(matchSearch)
      );
    });
  };

  // Count linked instruments for a tag
  const getLinkedCount = (id) => {
    return instruments.filter(i => String(i.details?.tag_id) === String(id)).length;
  };

  return (
    <div className="sterile-container" style={{ margin: '0 auto', maxWidth: '1200px', paddingBottom: '40px' }}>
      
      {/* Main Grid: Add Tag Form & Link Tool Form */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
        
        {/* Card 1: Tag Editor Form */}
        <div id="tag-editor-form" className="sterile-card" style={{ textAlign: 'left' }}>
          <div className="sterile-header">
            <h2>{isEditing ? '📝 แก้ไขรายละเอียด Tag' : '🔖 เพิ่มประเภท Tag เครื่องมือ'}</h2>
            <p>ระบุชื่อ รายละเอียด และรูปภาพของ Tag เครื่องมือแต่ละชนิด</p>
            <div className="accent-bar" style={{ margin: '10px 0 0 0' }}></div>
          </div>

          <form onSubmit={handleSaveTag} style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontWeight: '600', color: 'rgba(255,255,255,0.85)', fontSize: '0.9em' }}>ชื่อ Tag (Tag Name) <span style={{ color: '#ff4d4d' }}>*</span></label>
              <input
                type="text"
                placeholder="เช่น ชุดตรวจฟัน, คีม Anterior, หัวกรอเร็ว"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isEditing && name.toUpperCase() === 'NON-STERILE'}
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: (isEditing && name.toUpperCase() === 'NON-STERILE') ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
                  color: (isEditing && name.toUpperCase() === 'NON-STERILE') ? 'rgba(255,255,255,0.4)' : '#fff',
                  outline: 'none',
                  fontSize: '0.95em',
                  cursor: (isEditing && name.toUpperCase() === 'NON-STERILE') ? 'not-allowed' : 'text'
                }}
                required
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontWeight: '600', color: 'rgba(255,255,255,0.85)', fontSize: '0.9em' }}>รายละเอียด / คำอธิบาย</label>
              <textarea
                placeholder="เช่น แถบคาดสีเหลือง สำหรับคลินิกศัลยกรรม..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  outline: 'none',
                  fontSize: '0.95em',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontWeight: '600', color: 'rgba(255,255,255,0.85)', fontSize: '0.9em' }}>อัปโหลดรูปภาพ Tag</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  style={{ display: 'none' }}
                  id="tag-file-upload-input"
                />
                <label
                  htmlFor="tag-file-upload-input"
                  style={{
                    padding: '12px 20px',
                    background: isUploading ? '#666' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    borderRadius: '10px',
                    cursor: isUploading ? 'wait' : 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9em',
                    boxShadow: '0 4px 10px rgba(102,126,234,0.3)',
                    display: 'inline-block',
                    textAlign: 'center',
                    flex: 1
                  }}
                >
                  {isUploading ? '⏳ กำลังอัปโหลด...' : '📷 เลือกรูปถ่าย Tag'}
                </label>
                {imageUrl && (
                  <button
                    type="button"
                    onClick={() => setImageUrl('')}
                    style={{
                      background: 'rgba(231, 76, 60, 0.2)',
                      border: '1px solid #e74c3c',
                      color: '#e74c3c',
                      borderRadius: '10px',
                      padding: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                    title="ลบรูปภาพ"
                  >
                    ✕
                  </button>
                )}
              </div>

              {imageUrl ? (
                <div style={{ marginTop: '15px', position: 'relative', width: '120px', height: '120px', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.2)', overflow: 'hidden' }}>
                  <img
                    src={imageUrl}
                    alt="Preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ) : (
                <div style={{
                  marginTop: '15px',
                  height: '120px',
                  borderRadius: '12px',
                  border: '2px dashed rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '0.85em'
                }}>
                  ยังไม่ได้เลือกรูปภาพ
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                type="submit"
                disabled={submitting || isUploading}
                style={{
                  flex: 2,
                  padding: '14px',
                  background: submitting ? '#ccc' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: submitting ? 'wait' : 'pointer',
                  fontSize: '1em',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(56, 239, 125, 0.2)'
                }}
              >
                {submitting ? '⏳ กำลังบันทึก...' : isEditing ? '💾 อัปเดต Tag' : '💾 บันทึกประเภท Tag'}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1em',
                    fontWeight: 'bold'
                  }}
                >
                  ยกเลิก
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Card 2: Link Tag with Instrument Barcode */}
        <div className="sterile-card" style={{ textAlign: 'left' }}>
          <div className="sterile-header">
            <h2>🔗 ผูกบาร์โค้ดวัสดุเข้ากับ Tag</h2>
            <p>นำรูปภาพ Tag ที่สร้างไว้ไปเชื่อมโยงกับรหัสบาร์โค้ดของเครื่องมือ/พัสดุ</p>
            <div className="accent-bar" style={{ margin: '10px 0 0 0' }}></div>
          </div>

          <form onSubmit={handleLinkBarcode} style={{ display: 'flex', flexDirection: 'column', gap: '18px', marginTop: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontWeight: '600', color: 'rgba(255,255,255,0.85)', fontSize: '0.9em' }}>1. เลือก Tag ที่ต้องการเชื่อมโยง <span style={{ color: '#ff4d4d' }}>*</span></label>
              <select
                value={selectedTagId}
                onChange={(e) => setSelectedTagId(e.target.value)}
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(15, 12, 41, 0.95)',
                  color: '#fff',
                  outline: 'none',
                  fontSize: '0.95em',
                  cursor: 'pointer'
                }}
                required
              >
                <option value="">-- เลือกประเภท Tag --</option>
                {tags.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontWeight: '600', color: 'rgba(255,255,255,0.85)', fontSize: '0.9em' }}>2. ระบุหรือสแกนบาร์โค้ดอุปกรณ์ (Unique Barcode) <span style={{ color: '#ff4d4d' }}>*</span></label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="สแกน หรือพิมพ์รหัสบาร์โค้ด..."
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    outline: 'none',
                    fontSize: '1em',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    letterSpacing: '1px'
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setIsScannerOpen(true)}
                  style={{
                    padding: '0 18px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '1.4em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="เปิดกล้องสแกนบาร์โค้ด"
                >
                  📷
                </button>
              </div>
            </div>

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1.05em',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                marginTop: '10px'
              }}
            >
              🔗 ผูกบาร์โค้ดเข้ากับ Tag นี้
            </button>
          </form>
        </div>

      </div>

      {/* 🔖 Section 2: Display List of Tag Cards */}
      <div className="sterile-card" style={{ marginTop: '30px', textAlign: 'left' }}>
        <div className="sterile-header">
          <h2>📋 รายการประเภท Tag ในระบบ ({tags.length} ชนิด)</h2>
          <p>ประเภทของ Tag ทั้งหมดที่มี สามารถแก้ไข รายละเอียด หรือลบได้จากตารางด้านล่างนี้</p>
          <div className="accent-bar" style={{ margin: '10px 0 0 0' }}></div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>⏳ กำลังโหลดข้อมูล...</div>
        ) : tags.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>ยังไม่มีรายการประเภท Tag ในระบบ</div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
            marginTop: '25px'
          }}>
            {tags.map(tag => {
              const count = getLinkedCount(tag.id);
              return (
                <div
                  key={tag.id}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '16px',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    position: 'relative',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                    <div style={{ width: '80px', height: '80px', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0, position: 'relative', background: '#fff' }}>
                      {tag.image_url ? (
                        <img src={tag.image_url} alt={tag.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8em', background: '#eceff1', color: '#b0bec5' }}>
                          🛠️
                        </div>
                      )}
                      {tag.name === 'NON-STERILE' && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: '#ffffff',
                          border: '1.5px solid #000000',
                          padding: '2px 4px',
                          fontSize: '0.45em',
                          fontWeight: 'bold',
                          color: '#000000',
                          letterSpacing: '0.5px',
                          fontFamily: 'Arial, sans-serif',
                          boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
                          whiteSpace: 'nowrap',
                          zIndex: 10
                        }}>
                          NON-STERILE
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 6px 0', fontSize: '1.05em', color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {tag.name}
                        {tag.name === 'NON-STERILE' && (
                          <span style={{ fontSize: '0.65em', background: 'rgba(230, 126, 34, 0.2)', color: '#e67e22', padding: '2px 6px', borderRadius: '4px', border: '1px solid rgba(230, 126, 34, 0.3)' }}>
                            Tag ระบบ
                          </span>
                        )}
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.85em', color: 'rgba(255,255,255,0.5)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.4' }}>
                        {tag.description || 'ไม่มีคำอธิบาย'}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '12px', marginTop: 'auto' }}>
                    <span style={{ fontSize: '0.82em', background: 'rgba(102, 126, 234, 0.15)', color: '#a5b4fc', padding: '3px 8px', borderRadius: '8px', fontWeight: 'bold' }}>
                      🔗 ผูกแล้ว {count} ชิ้น
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEditClick(tag)}
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: 'none',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.82em',
                          fontWeight: 'bold'
                        }}
                      >
                        ✏️ แก้ไข
                      </button>
                      <button
                        onClick={() => handleDeleteTag(tag.id)}
                        disabled={tag.name === 'NON-STERILE'}
                        style={{
                          background: tag.name === 'NON-STERILE' ? 'rgba(255,255,255,0.02)' : 'rgba(231, 76, 60, 0.1)',
                          border: 'none',
                          color: tag.name === 'NON-STERILE' ? 'rgba(255,255,255,0.2)' : '#e74c3c',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: tag.name === 'NON-STERILE' ? 'not-allowed' : 'pointer',
                          fontSize: '0.82em',
                          fontWeight: 'bold'
                        }}
                      >
                        🗑️ ลบ
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 🔗 Section 3: List of Current Barcode-Tag Associations */}
      <div className="sterile-card" style={{ marginTop: '30px', textAlign: 'left' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div className="sterile-header" style={{ marginBottom: 0 }}>
            <h2>🔗 รายการเครื่องมือที่ผูก Tag ไว้ ({getLinkedInstruments().length} บาร์โค้ด)</h2>
            <p>รายการบาร์โค้ดเครื่องมือทั้งหมดในระบบที่ได้รับการผูกรูปภาพ Tag</p>
          </div>
          <input
            type="text"
            placeholder="🔍 ค้นหารหัสบาร์โค้ด ชื่อเครื่องมือ หรือ Tag..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              outline: 'none',
              fontSize: '0.9em',
              width: '250px'
            }}
          />
        </div>
        <div className="accent-bar" style={{ margin: '10px 0 20px 0' }}></div>

        {getLinkedInstruments().length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
            {filterSearch ? 'ไม่พบข้อมูลที่ตรงกับคำค้นหา' : 'ยังไม่มีเครื่องมือชิ้นใดผูก Tag ไว้'}
          </div>
        ) : (
          <div className="sterile-table-container">
            <table className="sterile-table">
              <thead>
                <tr>
                  <th>บาร์โค้ด (Barcode)</th>
                  <th>ชื่อเครื่องมือ</th>
                  <th>สถานะปัจจุบัน</th>
                  <th>รายละเอียด Tag ที่ผูก</th>
                  <th style={{ textAlign: 'center' }}>รูปภาพ Tag</th>
                  <th style={{ textAlign: 'center' }}>การจัดการ</th>
                </tr>
              </thead>
              <tbody>
                {getLinkedInstruments().map(instr => {
                  const tag = tags.find(t => String(t.id) === String(instr.details?.tag_id));
                  return (
                    <tr key={instr.id}>
                      <td style={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '1.05em' }}>{instr.unique_barcode}</td>
                      <td>{instr.item_name}</td>
                      <td>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '0.82em',
                          fontWeight: 'bold',
                          background: instr.status === 'sterile-ready' ? 'rgba(46, 204, 113, 0.15)' : 'rgba(230, 126, 34, 0.15)',
                          color: instr.status === 'sterile-ready' ? '#2ecc71' : '#e67e22'
                        }}>
                          {instr.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 'bold', color: '#fff' }}>{tag ? tag.name : `ไม่พบประเภท Tag (ID: ${instr.details.tag_id})`}</div>
                        <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.5)' }}>{tag ? tag.description : ''}</div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {tag && tag.image_url ? (
                          <img
                            src={tag.image_url}
                            alt={tag.name}
                            style={{ width: '45px', height: '45px', borderRadius: '6px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => handleUnlinkBarcode(instr)}
                          style={{
                            background: 'rgba(231, 76, 60, 0.15)',
                            border: '1px solid rgba(231, 76, 60, 0.3)',
                            color: '#e74c3c',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.82em',
                            fontWeight: 'bold'
                          }}
                        >
                          🔗 ยกเลิกผูก Tag
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Camera Scanner Popup Modal */}
      {isScannerOpen && (
        <CameraScanner
          onScanSuccess={handleScannerSuccess}
          onClose={() => setIsScannerOpen(false)}
        />
      )}
    </div>
  );
};

export default InstrumentTags;
